import { useMemo } from 'react';

import { PendingInviteBulkAddResponseDto } from '../../../../backend/src/auth/pending-matter-invite/dto/pending-invite-bulk-add.response.dto';
import { AddUsersDialog, parseEmailRecipients } from '@/new/components/add-users-dialog';
import { Icon } from '@/new/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

type RecipientStatus = 'already-in-matter' | 'already-in-org' | 'needs-email-invitation';

export const parseInviteRecipients = parseEmailRecipients;

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
  existingMatterUserEmails?: string[];
  organisationUserEmails?: string[];
  onInvite: (emails: string[]) => Promise<PendingInviteBulkAddResponseDto>;
}

export function InviteUserDialog({
  open,
  onOpenChange,
  defaultEmail = '',
  existingMatterUserEmails = [],
  organisationUserEmails = [],
  onInvite,
}: InviteUserDialogProps) {
  const existingMatterEmails = useMemo(
    () => new Set(existingMatterUserEmails.map((email) => email.toLowerCase())),
    [existingMatterUserEmails],
  );
  const organisationEmails = useMemo(
    () => new Set(organisationUserEmails.map((email) => email.toLowerCase())),
    [organisationUserEmails],
  );
  const getRecipientStatus = (email: string): RecipientStatus =>
    existingMatterEmails.has(email)
      ? 'already-in-matter'
      : organisationEmails.has(email)
        ? 'already-in-org'
        : 'needs-email-invitation';

  return (
    <AddUsersDialog
      open={open}
      onOpenChange={onOpenChange}
      defaultEmail={defaultEmail}
      maxRecipients={1000}
      title={
        <>
          <Icon className="lucide--user-plus" /> Invite users to Wexler
        </>
      }
      description={
        <>
          <p>If a user is already in your organisation, they'll be added to this Matter straight away.</p>
          <p>
            If a user isn't on Wexler yet, they'll be sent an email invitation and be added to this Matter when their
            account is provisioned.{' '}
          </p>
        </>
      }
      inputButtonLabel="Add users"
      submitButtonLabel={(count) => `Send invitations or add to Matter (${count})`}
      submitErrorMessage="Invitations could not be sent. Please try again."
      getRecipientPresentation={(email) => {
        const status = getRecipientStatus(email);
        return {
          disabled: status === 'already-in-matter',
          emailSuffix:
            status === 'already-in-matter' ? (
              <span className="text-gray-400"> (already in this Matter)</span>
            ) : undefined,
          badge:
            status === 'needs-email-invitation' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500"
                    tabIndex={0}
                  >
                    <Icon className="lucide--send" />
                    Email Invitation
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[18rem] text-center">
                  This user doesn't have an account yet. They'll receive an email invitation and be added to this Matter
                  automatically on sign in.
                </TooltipContent>
              </Tooltip>
            ) : status === 'already-in-org' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500"
                    tabIndex={0}
                  >
                    <Icon className="lucide--building-2" />
                    In your organisation
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[18rem] text-center">
                  This user is already in your organisation and will be added to this Matter
                </TooltipContent>
              </Tooltip>
            ) : undefined,
        };
      }}
      onSubmit={async (emails) => {
        const response = await onInvite(emails);
        return {
          failedEmails: response.results.filter(({ status }) => status === 'failed').map(({ userEmail }) => userEmail),
        };
      }}
    />
  );
}
